'use client'

import { useState } from 'react'
import AffiliateLink from '@/components/common/AffiliateLink'
import { t } from '@/i18n/translations'

/**
 * "Gallery · Field" promo-code cards with reveal/copy (.gf-promos → .pm).
 * Shared by the country/service pages and the eSIM promotions/service pages.
 * `promotions` is pre-mapped on the server (see mapPromoCodes):
 *   { title, description, provider, providerCode, providerLogo, code, validUntil, link }.
 *
 * @param {string} vertical    optional AffiliateLink vertical (e.g. 'esim')
 * @param {string} emptyLabel  optional empty-state text (renders .cs-empty when no promos)
 */
export default function GfPromoCodes({ promotions = [], lang = 'en', country = '', vertical, emptyLabel }) {
    const ui = t(lang, 'countryWidgetsUi')
    const [shown, setShown] = useState({})
    const [copied, setCopied] = useState({})

    const reveal = (i) => setShown((s) => ({ ...s, [i]: true }))
    const copy = (code, i) => {
        try { navigator.clipboard?.writeText(code) } catch { /* noop */ }
        setCopied((c) => ({ ...c, [i]: true }))
        setTimeout(() => setCopied((c) => ({ ...c, [i]: false })), 1500)
    }

    return (
        <div className="gf-promos">
            {promotions.map((promo, i) => {
                const isShown = shown[i]
                const isCopied = copied[i]
                return (
                    <article className="pm" key={promo.code || i}>
                        <span className="who">
                            {promo.providerLogo && (
                                <span className="who-logo">
                                    <img src={promo.providerLogo} alt="" aria-hidden="true" />
                                </span>
                            )}
                            <span className="who-name">{promo.provider}</span>
                        </span>
                        <h3>{promo.title}</h3>
                        {promo.description && <p>{promo.description}</p>}
                        <div className="code">
                            <span className={`v ${isShown ? '' : 'hidden'}`}>{isShown ? promo.code : '• • • • • •'}</span>
                            <button
                                type="button"
                                onClick={() => (isShown ? copy(promo.code, i) : reveal(i))}
                            >
                                {isShown
                                    ? (isCopied ? ui.promo.copied : ui.promo.copy)
                                    : ui.promo.show}
                            </button>
                        </div>
                        {promo.validUntil && (
                            <span className="exp">{ui.promo.validUntil}{promo.validUntil}</span>
                        )}
                        {promo.link && promo.link !== '#' && (
                            <AffiliateLink
                                href={promo.link}
                                provider={promo.providerCode}
                                vertical={vertical}
                                country={country}
                                position="promo"
                                className="gf-alllink"
                                style={{ marginTop: 14 }}
                            >
                                {ui.promo.use}
                            </AffiliateLink>
                        )}
                    </article>
                )
            })}
            {promotions.length === 0 && emptyLabel && (
                <div className="cs-empty">{emptyLabel}</div>
            )}
        </div>
    )
}
